import Icon from '@mui/material/Icon';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import React from 'react';
import {getLogger, THEMEKEY} from '@olympeio/core';
import format from 'date-fns/format';
import { registerDataGridRenderer } from './DataGridRegistry.jsx';
import Tooltip from '@mui/material/Tooltip';
import Chip from '@mui/material/Chip';
import {Theme} from 'olympe';
import { markdownTextToReactElement } from '@olympeio/core';



registerDataGridRenderer({
    name: 'TEXT',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : 'NO VALUE';
        const mapping = options?.mapping;
        const firstBoldLine = options?.firstBoldLine ?? false;
        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {

                    const strValue = mapping ? mapping[value?.toString()] : value?.toString();
                    let finalValue = strValue ?? placeholder;
                    if (firstBoldLine && index === 0) {
                        finalValue = `<b>${finalValue}</b>`;
                        finalValue = markdownTextToReactElement(finalValue, 'span');
                    }

                    // If several values are provided we put the separator after each except the last item
                    if (index < values.length - 1) {
                        return [finalValue, lineBreak ? <br key={index} /> : separator];
                    } else {
                        return [finalValue];
                    }
                })}
            </Typography>
        );
    }
});



registerDataGridRenderer({
    name: 'NUMBER',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : ' - ';
        const digits = options?.digits !== undefined ? options.digits : 2;
        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {
                    const finalValue = value?.toFixed(digits) ?? placeholder;

                    // Done
                    return index < values.length - 1
                        ? [finalValue, lineBreak ? <br key={index} /> : separator]
                        : [finalValue];
                })}
            </Typography>
        );
    }
});



registerDataGridRenderer({
    name: 'CURRENCY',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : ' - ';
        const countryCode = options?.countryCode !== undefined ? options.countryCode : 'fr-CH';
        const currency = options?.currency !== undefined ? options.currency : 'CHF';
        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {
                    let finalValue = placeholder;
                    if (value !== null && value !== undefined) {
                        finalValue = new Intl.NumberFormat(countryCode, { style: 'currency', currency: currency }).format(value);
                    }

                    // Done
                    return index < values.length - 1
                        ? [finalValue, lineBreak ? <br key={index} /> : separator]
                        : [finalValue];
                })}
            </Typography>
        );
    }
});



registerDataGridRenderer({
    name: 'PERCENT',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : ' - ';
        const digits = options?.digits !== undefined ? options.digits : 1;
        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {
                    const finalValue = (value?.toFixed(digits) ?? placeholder) + '%';

                    // Done
                    return index < values.length - 1
                        ? [finalValue, lineBreak ? <br key={index} /> : separator]
                        : [finalValue];
                })}
            </Typography>
        );
    }
});



registerDataGridRenderer({
    name: 'BOOLEAN',
    render: (values, options) => {
        const trueIcon = options?.trueIcon !== undefined ? options.trueIcon : 'check';
        const falseIcon = options?.falseIcon !== undefined ? options.falseIcon : 'close';
        const setNullToFalse = options?.setNullToFalse !== undefined ? options.setNullToFalse : false;

        let icon = '';
        if (values[0] === true || values[0] === 'true') {
            icon = trueIcon;
        } else if (values[0] === false || values[0] === 'false') {
            icon = falseIcon;
        } else if (setNullToFalse && (values[0] === null || values[0] === undefined)) {
            icon = falseIcon;
        }

        return (
            <Icon>{icon}</Icon>
        );
    }
});



registerDataGridRenderer({
    name: 'DATE',
    render: (values, options) => {
        const lineBreak = options?.lineBreak !== undefined ? options.lineBreak : true;
        const separator = options?.separator !== undefined ? options.separator : ' ';
        const placeholder = options?.placeholder !== undefined ? options.placeholder : ' - ';
        const formatting = options?.format !== undefined ? options.format : 'dd.MM.yyyy';

        return (
            <Typography variant={'body2'}>
                {values.flatMap((value, index) => {
                    let finalValue = placeholder;
                    if (value instanceof Date) {
                        finalValue = format(value, formatting);
                    } else if (typeof value === 'string') {
                        finalValue = format(new Date(value), formatting);
                    }

                    // Done
                    return index < values.length - 1
                        ? [finalValue, lineBreak ? <br key={index} /> : separator]
                        : [finalValue];
                })}
            </Typography>
        );
    }
});



registerDataGridRenderer({
    name: 'CHIP',
    render: (values, options, _, $) => {
        const [innerValue] = values;
        const value = Array.isArray(innerValue) ? innerValue[0] : innerValue;
        const placeholder = options?.placeholder ?? '';
        const prefilledValue = options?.rules && options?.rules[value];
        const tooltipText = options?.tooltip?.value;
        const tooltipCondition = options?.tooltip?.condition;
        let bgColor, color, finalValue, border;

        color = prefilledValue?.color ?? options?.color;
        if (!color) {
            color = 'info';
        }
        if(['primary', 'secondary', 'success', 'info', 'warning', 'error'].includes(color.toLowerCase())) {
            const theme = $.get(THEMEKEY, true)
            const colorValues = {
                primary: theme?.get(Theme.primaryColorProp)?.toHexString(),
                secondary: theme?.get(Theme.secondaryColorProp)?.toHexString(),
                error: theme?.get(Theme.errorColorProp)?.toHexString(),
                warning: theme?.get(Theme.warningColorProp)?.toHexString(),
                info: theme?.get(Theme.infoColorProp)?.toHexString(),
                success: theme?.get(Theme.successColorProp)?.toHexString(),
            };
            color = colorValues[color.toLowerCase()] ?? '#06A80B';
        }

        // Getting values of the chip out of the configuration
        bgColor = color + '30';
        border = (prefilledValue?.border ? prefilledValue?.border + ' ' + color : (options?.border ? options?.border + ' ' + color : null));
        finalValue = prefilledValue?.value || value;
        const sx = {
            border,
            backgroundColor: bgColor,
            color,
            textAlign: 'center',
            fontWeight: 600
        }

        if (finalValue) {
            if (
                tooltipText &&
                ((tooltipCondition && tooltipCondition === finalValue) || tooltipCondition === undefined)
            ) {
                return (
                    <Tooltip title={tooltipText} placement={'right'} arrow>
                        <Box sx={{ width: 1, height: 1, display: 'flex', alignItems: 'center' }}>
                            <Chip label={finalValue} sx={sx} />
                        </Box>
                    </Tooltip>
                );
            } else {
                return (
                    <Chip label={finalValue} sx={sx} />
                );
            }
        } else {
            return placeholder;
        }
    },
});


// /**
//  * ACTIONS
//  * Render buttons and register the onClick event on them
//  * - actions: Array<string> = []
//  * - disable: object, eg: {"forecastStatus":["ACP"]}
//  * - disabled: boolean
//  */
// registerDataGridRenderer({
//     name: 'ACTIONS',
//     render: (_values, options, object, $) => {
//         const actions = options?.actions || [];
//         let disabled = options?.disabled;
//         if (options?.disable) {
//             // there are rules defined
//             for (const [property, disabledValues] of Object.entries(options.disable)) {
//                 const value = getValueFromPath(object, property);
//                 if (disabledValues.includes(value)) {
//                     disabled = true;
//                     break;
//                 }
//             }
//         }
//         return (
//             <div>
//                 {actions.map((action, index) => {
//                     return (
//                         <Button
//                             key={index}
//                             variant={'text'}
//                             disabled={disabled}
//                             sx={{ textDecoration: 'underline', '&:hover': { textDecoration: 'underline' } }}
//                             onClick={() => {
//                                 $.set('Action Clicked', action);
//                                 $.set('Actioned Object', object);
//                                 $.trigger('On Action Clicked');
//                             }}
//                         >
//                             {action}
//                         </Button>
//                     );
//                 })}
//             </div>
//         );
//     },
// });



/**
 * ICON_ACTIONS
 * Render icon buttons and register the onClick event on them
 * - actions: Array<string> = []
 * - colorize: {string: string} optional
 */
registerDataGridRenderer({
    name: 'ICON_ACTIONS',
    render: (values, options, object, $) => {
        const actions = options?.actions || [];

        return (
            <div>
                {actions.map((action, index) => {
                    let condition = false;

                    if (action?.hiddenCondition !== undefined && action?.hiddenCondition === values[0]) {
                        condition = true;
                    }
                    const color = action?.color ?? 'primary';
                    const icon = action?.icon ?? 'info';


                    return condition ? (
                        <></>
                    ) : (
                        <IconButton
                            key={index}
                            color={color}
                            onClick={() => {
                                const eventHandler = $.get('On Contextual Event');
                                if (eventHandler) {

                                    const onContextual$ = $.runner($.get('On Contextual Event'));
                                    const [CtrlFlowInput, eventTypeInput, objectInput] = $.get('On Contextual Event').getInputs();

                                    onContextual$
                                        .set(eventTypeInput, action?.eventType)
                                        .set(objectInput, object)
                                        .trigger(CtrlFlowInput);
                                } else {
                                    getLogger('DataGrid').warn('Please define a renderer to catch the event when you click on an action icon');
                                }
                            }}
                        >
                            <Icon>{icon}</Icon>
                        </IconButton>
                    );
                })}
            </div>
        );
    },
});
